import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import { message } from 'antd'

import type {
  BulkOrderUpdateJobListResponse,
  BulkOrderUpdateJobResponse,
  CancelBulkOrderUpdateJobRequest,
  CancelBulkOrderUpdateJobResponse,
  CreateBulkOrderUpdateJobRequest,
  CreateBulkOrderUpdateJobResponse,
  GetBulkOrderUpdateJobRequest,
  ListBulkOrderUpdateJobsRequest,
} from './types'

export const useBulkOrderUpdate = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  // Query to list bulk order update jobs
  const useListJobsQuery = (params?: ListBulkOrderUpdateJobsRequest) =>
    useQuery({
      queryKey: ['bulk-order-update-jobs', params],
      queryFn: async ({ queryKey }): Promise<BulkOrderUpdateJobListResponse> => {
        const requestParams = queryKey[1] as ListBulkOrderUpdateJobsRequest

        const searchParams = new URLSearchParams()
        if (requestParams?.limit) searchParams.append('limit', requestParams.limit.toString())
        if (requestParams?.offset) searchParams.append('offset', requestParams.offset.toString())
        if (requestParams?.status) searchParams.append('status', requestParams.status)
        if (requestParams?.created_by) searchParams.append('created_by', requestParams.created_by)

        return api.post('bulk_order_update/list', {}, { queryParams: searchParams })
      },
      refetchInterval: 5000, // Refresh every 5 seconds for real-time updates
      refetchOnWindowFocus: false,
    })

  // Query to get a specific bulk order update job
  const useGetJobQuery = (jobId?: string) =>
    useQuery({
      queryKey: ['bulk-order-update-job', jobId],
      queryFn: ({ queryKey }): Promise<BulkOrderUpdateJobResponse> => {
        const id = queryKey[1] as string
        return api.post('bulk_order_update/get', { job_id: id })
      },
      enabled: !!jobId,
      refetchInterval: 2000, // Always poll for logs updates
      refetchOnWindowFocus: false,
    })

  // Mutation to create a new bulk order update job
  const createJobMutation = useMutation({
    mutationFn: (request: CreateBulkOrderUpdateJobRequest): Promise<CreateBulkOrderUpdateJobResponse> =>
      api.post('bulk_order_update/create', request),
    onSuccess: (data) => {
      message.success(`Job created successfully: ${data.job_id}`)
      queryClient.invalidateQueries({ queryKey: ['bulk-order-update-jobs'] })
    },
    onError: (error: any) => {
      message.error(`Failed to create job: ${error.response?.data?.detail || error.message}`)
    },
  })

  // Mutation to cancel a bulk order update job
  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string): Promise<CancelBulkOrderUpdateJobResponse> =>
      api.post('bulk_order_update/cancel', { job_id: jobId }),
    onSuccess: (data, jobId) => {
      message.success('Job cancelled successfully')
      queryClient.invalidateQueries({ queryKey: ['bulk-order-update-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['bulk-order-update-job', jobId] })
    },
    onError: (error: any) => {
      message.error(`Failed to cancel job: ${error.response?.data?.detail || error.message}`)
    },
  })

  return {
    // Query hooks
    useListJobsQuery,
    useGetJobQuery,

    // Mutation hooks
    createJobMutation,
    cancelJobMutation,
  }
}
